<template>
    <div>
        <a
            href="#"
            class="action-menu-link"
            :class="{ 'signed': !signingNeeded }"
            :disabled="loading || noFilesToSign"
            @click.prevent="(loading || noFilesToSign) ? null : showModal = true"
            v-show="showButton"
        >
            <b-icon :icon="signingNeeded ? 'file-alt' : 'check'" class="mr-2" size="is-small" />
            <span>{{ noFilesToSign ? 'Geen documenten te tekenen' : signingNeeded ? 'Documenten tekenen' : 'Documenten getekend' }}</span>
        </a>

        <b-modal :active.sync="showQR" id="contractSignDocumentsModalQR" aria-modal :width="600" :can-cancel="true" style="z-index:99999999;text-align:center;">
            <div class="modal-card-body" style="border-radius:4px;">
                <b-notification type="is-success" role="alert" v-if="QRPageOpened">
                    De pagina is geopend, deze pop-up kan nu gesloten worden.
                </b-notification>
                <p v-else>Scan de QR code met je telefoon om naar de webpagina te gaan om te tekenen.</p>

                <qrcode-vue :value="qr" :size="500" level="H" />
                <br>
                <a :href="qr" target="_blank" class="button is-primary is-small" @click="showQR = false">Open de tekenpagina op dit apparaat</a>
            </div>
            <footer class="modal-card-foot text-right">
                <b-button type="is-light" @click="showQR = false">Sluiten</b-button>
            </footer>
        </b-modal>

        <b-modal :active.sync="showModal" id="contractSignDocumentsModal" aria-modal :width="600" :can-cancel="true" :key="refreshKey">
            <div class="modal-card" style="width: auto">
                <header class="modal-card-head">
                    <p class="modal-card-title">{{ contractHold == 1 ? 'Contractondertekening vereist' : 'Documenten tekenen' }}</p>
                </header>
            </div>
            <section class="modal-card-body" style="overflow: hidden;border-radius:0;text-align:left;">
                <!-- Customer signing mode (contractHold == 1) -->
                <div v-if="contractHold == 1">
                    <b-notification type="is-success" :closable="false" v-if="isNewContract">
                        <article class="media">
                            <figure class="media-left">
                                <b-icon icon="envelope" size="is-large"></b-icon>
                            </figure>
                            <div class="media-content">
                                <div class="content">
                                    <h3 style="margin: .6rem 0;color: white;">
                                        <strong>De ondertekeningsmail is verstuurd!</strong>
                                    </h3>
                                </div>
                            </div>
                        </article>
                    </b-notification>

                    <h4>
                        <strong>Wat gebeurt er nu?</strong>
                    </h4>
                    <ul style="margin-left: 20px; margin-top: 10px;">
                        <li>De klant heeft een e-mail ontvangen met een link om de contractdocumenten digitaal te ondertekenen.</li>
                        <li>Zodra alle documenten zijn ondertekend, wordt het contract automatisch actief, het volledige contract wordt dan direct per e-mail naar de klant verstuurd.</li>
                        <li>Eventuele incassobetalingen worden automatisch uitgevoerd na ondertekening.</li>
                    </ul>

                    <b-notification type="is-info" role="alert">
                        <p class="marginbottom0">Het is ook mogelijk om het contract samen met de klant te ondertekenen. De klant kan hiervoor de QR-code met zijn telefoon scannen. Klik hiervoor op "Documenten tekenen".</p>
                    </b-notification>
                </div>

                <!-- Organization signing mode (contractHold == 0) -->
                <div v-else>
                    <p v-if="signingNeeded">
                        {{ signingNeeded == 1 ? 'Er is nog een document dat ondertekend moet worden' : 'Er zijn nog enkele documenten die ondertekend moeten worden' }}&nbsp;voor voltooiing van het contract. Klik op "Document(en) tekenen" om een QR-code te genereren waarmee eenvoudig een telefoon gebruikt kan worden om {{ signingNeeded == 1 ? 'het document' : 'de documenten' }} te ondertekenen.
                        <br>
                        Als de bestanden op een andere manier al zijn getekend dan kan je de knop naast het document klikken om het te markeren als getekend.
                    </p>
                    <p v-if="signingNeeded">Klik op {{ contractHold == 1 ? '"Nogmaals versturen"' : '"Documenten mailen"' }} om de ondertekeningsmail {{ contractHold == 1 ? '(nogmaals)' : '' }} naar de klant te versturen.</p>
                    <p v-else>
                        <b-notification type="is-success">
                            Alle documenten zijn getekend!<br>
                            <small>Klik op het vinkje naast het document om het te markeren als ongetekend.</small>
                        </b-notification>
                    </p>
                    <p v-show="!!qr && signingNeeded">De QR code is gescant, er wordt elke 5 seconden gecontroleerd of {{ signingNeeded == 1 ? 'het document' : 'de documenten' }} getekend {{ signingNeeded == 1 ? 'is' : 'zijn' }}.</p>

                    <b-table :data="files" :loading="loading">
                        <template>
                            <b-table-column v-slot="props" field="name" label="Document">
                                {{ props.row.name }}
                            </b-table-column>
                            <b-table-column v-slot="props" field="signed" label="Getekend" numeric>
                                <span v-if="props.row.signed != 0">
                                    <b-tooltip label="Document is getekend! " position="is-left">
                                        <b-button type="is-success" size="is-small" icon-right="check" @click="markAsUnsigned(props.row.id)"></b-button>
                                    </b-tooltip>
                                </span>
                                <span v-else>
                                    <b-tooltip label="Markeren als getekend" position="is-left">
                                        <b-button type="is-success" outlined size="is-small" @click="markAsSigned(props.row.id)" icon-right="check"></b-button>
                                    </b-tooltip>
                                </span>
                            </b-table-column>
                        </template>
                    </b-table>
                </div>
            </section>
            <footer class="modal-card-foot text-right">
                <!-- Customer signing mode (contractHold == 1) -->
                <template v-if="contractHold == 1">
                    <b-button type="is-light" @click="showOrCreateQR">Documenten tekenen</b-button>
                    <b-button type="is-warning" v-show="signingNeeded" @click="sendSigningEmail" :loading="sendingEmail" :disabled="mailSent">Nogmaals versturen</b-button>
                    <b-button type="is-primary" @click="showModal = false">Sluiten</b-button>
                </template>
                <!-- Organization signing mode (contractHold == 0) -->
                <template v-else>
                    <b-button type="is-light" @click="showModal = false">Sluiten</b-button>
                    <b-button type="is-warning" v-show="signingNeeded" @click="sendSigningEmail" :loading="sendingEmail" :disabled="mailSent">Documenten mailen</b-button>
                    <b-button type="is-primary" v-show="signingNeeded" @click="showOrCreateQR">Documenten tekenen</b-button>
                </template>
            </footer>
        </b-modal>
    </div>
</template>

<script>
import toastMixins from '@/mixins/toastMixins.js'
import QrcodeVue from 'qrcode.vue'

export default {
    name: 'ContractsSignDocumentsModal',

    mixins: [toastMixins],

    components: {
        QrcodeVue
    },

    props: {
        showButton: {
            type: Boolean,
            required: false,
            default: true
        },
        contractId: {
            type: Number|String,
            required: true
        },
        includedFrom: {
            type: String,
            required: false
        },
        show: {
            type: Boolean,
            required: false,
            default: false
        },
        contractHold: {
            type: Number,
            required: false,
            default: 0
        },
        isNewContract: {
            type: Boolean,
            required: false,
            default: false
        }
    },

    data() {
        return {
            refreshKey: 0,
            intervalFiles: null,
            intervalQR: null,
            showModal: false,
            loading: false,
            files: [],
            showQR: false,
            qr: null,
            QRPageOpened: false,
            firstQRinit: true,
            noFilesToSign: false,
            sendingEmail: false,
            mailSent: false,
            emittedReloadForAllSigned: false,
        }
    },

    computed: {
        signingNeeded() {
            return _.filter(this.files, {signed: 0}).length
        }
    },

    created() {
        this.startUp()
    },

    methods: {
        startUp() {
            if (this.startingUp) return false;

            this.startingUp = true // lets make sure we only start one simultaneous request
            this.loading = true

            axios.get('/api/contract/'+this.contractId+'/sign-documents', {
                headers: {'Authorization': 'bearer ' + this.$store.state.authToken}
            }).then((response) => {
                this.noFilesToSign = !response.data.length

                // Show modal if included from connect or contract and files need to be signed.
                if (this.includedFrom && (this.includedFrom === 'connect' || this.includedFrom === 'contract') && response.data.length) {
                    if (this.$route.query && this.$route.query.nieuw) {
                        // Show modal for customer signing (contractHold == 1) regardless of files
                        // OR show modal for organization signing (contractHold == 0) if there are unsigned files
                        if (this.contractHold == 1 || !!response.data.filter((x) => {return x.signed === 0}).length) {
                            this.showModal = true
                        }
                    }
                }

                this.files = response.data
                this.loading = false
                this.refreshKey = this.refreshKey + 1
            }).catch((error) => {
                console.log(error)
            }).then(() => {
                this.startingUp = false
            })
        },

        markAsSigned(id){
            this.loading = true

            axios.post('/api/contract/'+this.contractId+'/sign-documents/'+id, {}, {
                headers: {'Authorization': 'bearer ' + this.$store.state.authToken}
            }).then((response) => {
                if (response?.data?.documents && Array.isArray(response.data.documents)) {
                    this.files = response.data.documents
                } else if (response?.data?.success) {
                    this.markContractSignedForUi()
                }
                this.loading = false
                this.startUp()
            }).catch(() => {
                this.refreshKey = this.refreshKey + 1
                this.showError('Er is iets fout gegaan! Probeer het opnieuw.')
                this.loading = false
                this.startUp()
            })
        },

        markAsUnsigned(id) {
            this.markAsSigned(id)
        },

        showOrCreateQR() {
            this.loading = true

            axios.post('/api/contract/'+this.contractId+'/sign-documents/qr', {}, {
                headers: {'Authorization': 'bearer ' + this.$store.state.authToken}
            }).then((response) => {
                this.loading = false

                // Set the QR
                this.qr = response.data.url

                // Close if its opened.
                if(response.data.status === 'opened' && this.showQR){
                    this.QRPageOpened = true
                    clearInterval(this.intervalQR)
                    return false
                } else {
                    this.showQR = true
                }

                if(this.firstQRinit) {
                    this.intervalQR = setInterval(() => {
                        this.showOrCreateQR()
                    }, 2500)

                    this.firstQRinit = false
                }
            }).catch(() => {
                this.showError('Er is iets fout gegaan! Probeer het opnieuw.')
                this.loading = false
            })
        },

        sendSigningEmail() {
            this.sendingEmail = true
            this.mailSent = false

            axios.post('/api/contract/'+this.contractId+'/send-signing-email', {}, {
                headers: {'Authorization': 'bearer ' + this.$store.state.authToken}
            }).then(() => {
                this.mailSent = true
                this.sendingEmail = false
                this.showMessage('De ondertekeningsmail is verzonden naar de klant.')

                if (this.$route.query.nieuw === 'true') {
                    const query = { ...this.$route.query }
                    delete query.nieuw
                    this.$router.replace({ query })
                    this.$emit('reload')
                }
            }).catch((e) => {
                this.showError('Er is iets fout gegaan! Probeer het opnieuw.')
                this.sendingEmail = false
            }).finally(() => {
                this.startUp()
            })
        },

        markContractSignedForUi() {
            this.files = (this.files || []).map(file => ({
                ...file,
                signed: file.signed && Number(file.signed) !== 0 ? file.signed : 1
            }))
        },

        toggleModal() {
            this.showModal = !this.showModal
        }
    },

    watch: {
        show(newVal) {
            if (newVal) {
                this.showModal = true
            }
        },

        showModal(newVal) {
            if (!newVal) {
                clearInterval(this.intervalFiles)
                clearInterval(this.intervalQR)
                this.firstQRinit = true
                this.emittedReloadForAllSigned = false

                if (!this.signingNeeded){
                    this.$emit('reload')
                }
            }
        },

        signingNeeded(newVal) {
            if (!newVal) {
                clearInterval(this.intervalFiles)
                clearInterval(this.intervalQR)
                this.firstQRinit = true
            }
        },

        files: {
            deep: true,
            handler(newFiles, oldFiles) {
                if (!Array.isArray(newFiles) || newFiles.length === 0) {
                    return
                }

                const hasAllSigned = newFiles.every(file => Number(file?.signed || 0) !== 0)
                const hadAllSigned = Array.isArray(oldFiles) && oldFiles.length > 0
                    ? oldFiles.every(file => Number(file?.signed || 0) !== 0)
                    : true

                if (hasAllSigned && !hadAllSigned && !this.emittedReloadForAllSigned) {
                    this.emittedReloadForAllSigned = true
                    this.$emit('reload')
                }
            }
        },

        showQR(newVal) {
            if (newVal) {
                // First clear any other running intervals
                clearInterval(this.intervalFiles)

                this.intervalFiles = setInterval(() => {
                    this.startUp()
                }, 5000)
            } else {
                this.QRPageOpened = false
                this.firstQRinit = true
                clearInterval(this.intervalQR)
            }
        }
    }
}
</script>

